import { useEffect, useState } from 'react';

import { getKnowYourCustomerService } from '../api/getKyc';
import { setKycTemplateVersion } from '../store/application/application';
import { Kyc, Question } from '../types/KycTypes';
import { useAppDispatch, useAppSelector } from './hooks';
import useSettings from './useSettings';
/**
 * Calls the "getKnowYourCustomerService"-function to retrieve KYC-questions
 * @param onlyPep - a boolean flag indicatiing whether to fetch only pep questions
 * @returns an object containing:
 * kycData: a list of the complete kyc survey response
 * Allquestions: a flattened list of all questions
 * isError: a boolean indicating whether an error occured during data fetching
 * isLoading: a boolean indicating whether the data is still being fetched
 */

const useGetKycService = (onlyPep: boolean) => {
    const dispatch = useAppDispatch();
    const { apiKey, proxyBaseUrl } = useSettings();
    const { authenticationData } = useAppSelector((state) => state.authentication);
    const [kycData, setKycData] = useState<Kyc | undefined>(undefined);
    const [allQuestions, setAllQuestions] = useState<Question[]>([]);
    const [isError, setIsError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        // Guards against writing state/dispatching after the wizard has moved on: `Pages` unmounts each page on
        // Next/Back, and a fetch that resolves after that would otherwise call these setters -- and, worse,
        // dispatch a stale kycTemplateVersion into the store, which is NOT covered by React's unmount no-op the
        // way local state is.
        let isMounted = true;

        setIsError(false);
        setIsLoading(true);
        getKnowYourCustomerService(apiKey, onlyPep, proxyBaseUrl, authenticationData)
            .then((pep) => {
                if (isMounted) {
                    setKycData(pep);
                    setAllQuestions(pep.groups.flatMap((group) => group.questions) ?? []);
                    // Kept in the store rather than local state: the answers to this survey are sent later, either
                    // on submit (PEP, before an application exists) or on save_survey_answers, and both read from
                    // the store.
                    dispatch(setKycTemplateVersion(pep.templateVersion));
                }
            })
            .catch(() => {
                if (isMounted) setIsError(true);
            })
            .finally(() => {
                if (isMounted) setIsLoading(false);
            });

        return () => {
            isMounted = false;
        };
        // Deliberately fetches once per mount. apiKey, proxyBaseUrl, authenticationData and onlyPep are left out of
        // the dependencies on purpose: re-running on a token refresh would fetch a fresh survey mid-form and
        // overwrite kycTemplateVersion with a newer template than the answers on screen were written against, which
        // is the exact mismatch this version exists to prevent. Every caller passes a literal onlyPep, so there is
        // nothing to refetch for; remounting this hook's owning page is the intended way to fetch a fresh survey.
        // This is a deliberate omission, not a missed dependency -- react-hooks/exhaustive-deps is off repo-wide,
        // so this should not be re-flagged by a future linter/bot.
    }, [setKycData, setAllQuestions, dispatch]);

    return { kycData, allQuestions, isError, isLoading };
};
export default useGetKycService;
